import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PiArrowSquareOutBold, PiXBold } from 'react-icons/pi';
import { Badge, Button, LoadingState, Text, Title } from '@v2/design-system';
import api from '@v2/lib/api';
import { fillBarColor } from './cd-slot-styles';
import { cn } from '@v2/utils/cn';

type SlotDetail = {
    code: string;
    found: boolean;
    hint: string | null;
    parsed: { rua: string; bay: number; level: number } | null;
    summary: {
        count: number;
        fill_pct: number;
        capacity_units: number;
        value_brl: number;
        primary_product_name: string | null;
        primary_product_code: string | null;
        primary_product_id: number | null;
        primary_order_number: string | null;
        products_count: number;
        orders_count: number;
        total_weight_kg: number;
        last_movement_label: string | null;
        cliente_count: number;
        estoque_count: number;
        days_label: string | null;
        overdue_count: number;
        overdue_charge_brl: number;
    };
    items: Array<{
        id: number;
        product_id: number | null;
        product_name: string | null;
        product_code: string | null;
        product_url_v2: string | null;
        serial_number: string | null;
        order_number: string | null;
        customer_name: string | null;
        tipo: string;
        days_at_cd: number;
        total_charge_brl: number;
        billable_days: number;
    }>;
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

type Props = {
    slotCode: string | null;
    onClose: () => void;
    warehouseSummary?: {
        total_slots: number;
        occupied_slots: number;
        free_slots: number;
        ruas_count: number;
    };
};

export default function CdSlotDetailPanel({ slotCode, onClose, warehouseSummary }: Props) {
    const { data, isLoading } = useQuery<SlotDetail>({
        queryKey: ['dashboard', 'estoque', 'slot', slotCode],
        enabled: !!slotCode,
        queryFn: async () => (await api.get(`/dashboard/estoque/slot/${encodeURIComponent(slotCode!)}`)).data,
    });

    if (!slotCode) {
        return (
            <aside className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 p-5 text-sm text-gray-500">
                <Title as="h6" className="text-sm font-semibold text-gray-700">
                    Detalhe da posição
                </Title>
                <Text className="mt-2 text-xs">
                    Clique em uma posição do mapa para ver pedido, equipamento, peso e lista de unidades.
                </Text>
                {warehouseSummary && (
                    <dl className="mt-6 space-y-2 text-xs">
                        <Row label="Ruas no mapa" value={String(warehouseSummary.ruas_count)} />
                        <Row label="Slots totais" value={String(warehouseSummary.total_slots)} />
                        <Row
                            label="Ocupados / livres"
                            value={`${warehouseSummary.occupied_slots} / ${warehouseSummary.free_slots}`}
                        />
                    </dl>
                )}
            </aside>
        );
    }

    if (isLoading) {
        return (
            <aside className="rounded-xl border border-gray-200 bg-white p-5">
                <LoadingState label="Carregando posição…" />
            </aside>
        );
    }

    if (!data?.found) {
        return (
            <aside className="rounded-xl border border-gray-200 bg-white p-5">
                <PanelHeader code={slotCode} onClose={onClose} />
                <Text className="mt-4 text-sm text-gray-500">
                    Posição não cadastrada no sistema ({slotCode}).
                </Text>
            </aside>
        );
    }

    const s = data.summary;
    const fillPct = s.fill_pct ?? 0;

    return (
        <aside className="sticky top-4 flex max-h-[calc(100vh-6rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-100 p-4">
                <PanelHeader code={data.code} onClose={onClose} />
                {data.parsed && (
                    <Text className="mt-1 text-xs text-gray-500">
                        Rua {data.parsed.rua} · Coluna {String(data.parsed.bay).padStart(2, '0')} ·{' '}
                        {['', '1º', '2º', '3º'][data.parsed.level] ?? data.parsed.level} andar
                    </Text>
                )}
                <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>Ocupação</span>
                        <span className="font-semibold tabular-nums">
                            {s.count} / {s.capacity_units} un. ({fillPct}%)
                        </span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-100">
                        <div
                            className={cn('h-full rounded-full', fillBarColor(fillPct))}
                            style={{ width: `${Math.max(fillPct, s.count > 0 ? 6 : 0)}%` }}
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {data.hint === 'livre' ? (
                    <Text className="text-sm text-emerald-700">Posição livre — sem unidades alocadas.</Text>
                ) : (
                    <>
                        <dl className="space-y-2 text-sm">
                            {s.primary_product_name && (
                                <div>
                                    <dt className="text-xs text-gray-500">Equipamento principal</dt>
                                    <dd className="font-semibold text-gray-900">{s.primary_product_name}</dd>
                                    {s.primary_product_code && (
                                        <dd className="text-xs text-gray-500">{s.primary_product_code}</dd>
                                    )}
                                </div>
                            )}
                            {s.primary_order_number && (
                                <Row label="Pedido" value={s.primary_order_number} />
                            )}
                            <Row label="Unidades" value={String(s.count)} />
                            <Row label="SKUs distintos" value={String(s.products_count)} />
                            <Row label="Pedidos" value={String(s.orders_count)} />
                            <Row label="Cliente / Estoque" value={`${s.cliente_count} / ${s.estoque_count}`} />
                            {s.total_weight_kg > 0 && (
                                <Row label="Peso total" value={`${s.total_weight_kg} kg`} />
                            )}
                            <Row label="Valor" value={brl(s.value_brl)} />
                            {s.days_label && <Row label="Tempo no slot" value={s.days_label} />}
                            {s.last_movement_label && (
                                <Row label="Última movimentação" value={s.last_movement_label} />
                            )}
                            {s.overdue_count > 0 && (
                                <Row
                                    label="Armazenagem em aberto"
                                    value={
                                        <span className="font-semibold text-rose-700">
                                            {s.overdue_count} · {brl(s.overdue_charge_brl)}
                                        </span>
                                    }
                                />
                            )}
                        </dl>

                        {s.primary_product_id && (
                            <Button
                                as={Link}
                                to={`/products/${s.primary_product_id}`}
                                variant="outline"
                                size="sm"
                                className="mt-4 w-full"
                            >
                                Ver produto
                                <PiArrowSquareOutBold className="ms-1 size-3.5" />
                            </Button>
                        )}

                        <Title as="h6" className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Unidades nesta posição
                        </Title>
                        <ul className="space-y-2">
                            {data.items.map((it) => (
                                <li
                                    key={it.id}
                                    className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-xs"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="font-medium text-gray-900">
                                            {it.product_name ?? '—'}
                                        </span>
                                        <Badge
                                            color={it.tipo === 'Cliente' ? 'info' : 'success'}
                                            variant="flat"
                                            rounded="pill"
                                            size="sm"
                                        >
                                            {it.tipo}
                                        </Badge>
                                    </div>
                                    <div className="mt-1 space-y-0.5 text-gray-600">
                                        {it.serial_number && <div>Serial: {it.serial_number}</div>}
                                        {it.order_number && <div>Pedido: {it.order_number}</div>}
                                        {it.customer_name && <div>{it.customer_name}</div>}
                                        <div>{it.days_at_cd}d no CD</div>
                                        {it.billable_days > 0 && (
                                            <div className="text-rose-700">
                                                Cobrança: {brl(it.total_charge_brl)}
                                            </div>
                                        )}
                                    </div>
                                    {it.product_url_v2 && (
                                        <Link
                                            to={it.product_url_v2.replace(/^\/v2(?=\/)/, '')}
                                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 hover:underline"
                                        >
                                            Abrir produto
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </aside>
    );
}

function PanelHeader({ code, onClose }: { code: string; onClose: () => void }) {
    return (
        <div className="flex items-start justify-between gap-2">
            <Title as="h6" className="font-mono text-base font-bold text-gray-900">
                {code}
            </Title>
            <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100"
                aria-label="Fechar"
            >
                <PiXBold className="size-4" />
            </button>
        </div>
    );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-right font-medium text-gray-900">{value}</dd>
        </div>
    );
}
